/**
 * PublicLanguageSelect
 *
 * Respondent-facing language picker shown on the public screener / panel
 * sign-up page when the survey is offered in more than one language. Unlike the
 * admin picker in `ScreenerAdminTools` (visible only to logged-in account
 * members), this is shown to everyone — including logged-out participants — so
 * a respondent can override the browser-auto-detected language. It labels each
 * option by its native name (e.g. "Español", "العربية") so a speaker can
 * recognize their language without reading English.
 *
 * Renders nothing when there is nothing to choose between (≤1 language or no
 * change handler), so call sites can mount it unconditionally.
 */

import React from 'react';
import { Label, Select } from '@rebass/forms';
import { FiGlobe } from 'react-icons/fi';
import { findLanguageMeta } from 'utils/surveyTranslations';

/**
 * @param {Object} props
 * @param {string[]} [props.languages]        available BCP-47 codes (default first)
 * @param {string} [props.currentLanguage]    currently-active code
 * @param {(value: string) => void} [props.onLanguageChange]
 * @param {'ltr'|'rtl'} [props.direction]     base text direction for the active language; the
 *                                            picker sits above the form and must read the same
 *                                            way round, or its label lands on the far side of
 *                                            the page from the form it belongs to
 * @param {React.CSSProperties} [props.style]
 */
export function PublicLanguageSelect({ languages, currentLanguage, onLanguageChange, direction, style }) {
    if (!Array.isArray(languages) || languages.length <= 1 || !onLanguageChange) return null;

    // A controlled <Select> whose value isn't one of its <option>s renders blank
    // on first paint. `currentLanguage` may be undefined (API returned no active
    // language) or a code not in `languages`, so fall back to the first offered
    // language — the same one the option list leads with.
    const selected = languages.includes(currentLanguage) ? currentLanguage : languages[0];

    return (
        <div className="no-print mb-6" style={style} dir={direction || undefined}>
            <Label
                htmlFor="public-language-select"
                className="flex items-center gap-2 fs-accent-12 color-text-secondary"
            >
                <FiGlobe aria-hidden="true" className="!m-0" /> Language
            </Label>
            <Select
                id="public-language-select"
                className="max-w-[260px]"
                value={selected}
                onChange={e => onLanguageChange(e.target.value)}
            >
                {languages.map(code => (
                    <option key={code} value={code}>
                        {findLanguageMeta(code).nativeLabel}
                    </option>
                ))}
            </Select>
        </div>
    );
}

export default PublicLanguageSelect;
